import { useTranslation } from 'next-i18next';
import VyButton from '../../../common/VyButton';
import { VyExportButton } from '../../../common/VyExportButton';
import VyTooltip from '../../../common/VyTooltip';
import { useContext, useState } from 'react';
import { useChat } from '../../../meeting/hooks/useChat';
import { clearStageMessages } from '../../../../services/services';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { MessageContext } from '.';
import { usePermission } from '../../../../hooks/usePermission';
import { VyConfirm } from '../../../common/VyConfirm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useMeetingPermission } from '../../../meeting/hooks/useMeetingPermission';

export interface ControlPanelProps {
  chatExportId?: string; // meetingId or pageId // TODO find new name
  isStage?: boolean;
  showClearChat?: boolean;
  showExport?: boolean;
  showHighlightsControl?: boolean;
  showChatLock?: boolean;
  showExternalLink?: boolean;
  isChatLocked?: boolean;
  style?: ControlPanelStyle;
}

export interface ControlPanelStyle {
  headerClassName?: string;
}

export const ControlPanel = ({
  chatExportId,
  isStage,
  showClearChat,
  showExport,
  showHighlightsControl,
  showChatLock,
  showExternalLink,
  isChatLocked,
  style,
}: ControlPanelProps) => {
  const page = useSelector((state) => state.common.pagedata);
  const event = useSelector((state) => state.common.event ?? state.admin.event);
  const user = useSelector((state) => state.common.user);

  const { showHighlights, setShowHighlights } = useContext(MessageContext);

  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

  const { t } = useTranslation();
  const { toggleChatLock, handleSendExternalChatLink, showQr, deleteMessages } = useChat();
  const { isEventHost } = usePermission();
  const { isAdmin: isMeetingAdmin } = useMeetingPermission();

  const isAdmin = isEventHost(user, event) || isMeetingAdmin();

  async function toggleShowHighlights() {
    setShowHighlights(!showHighlights);
  }

  async function clearChat() {
    if (isStage) clearStageMessages(page?._id!);
    else deleteMessages(chatExportId!);
  }

  async function handleToggleChat() {
    if (isStage) {
      toggleChatLock(page?._id);
    } else toggleChatLock();
  }

  if ((!isAdmin && isStage) || !event) return null;

  return (
    <div className="bg-inherit">
      <button
        type="button"
        className={`flex items-center bg-inherit justify-between w-full py-5 font-medium rtl:text-right text-gray-500  rounded-t-xl gap-3 ${style?.headerClassName}`}
        onClick={() => setIsAccordionExpanded(!isAccordionExpanded)}>
        <p className={`text-base`}>Control Panel</p>
        <FontAwesomeIcon icon={faChevronDown} className={`w-4 h-4 shrink-0 ${isAccordionExpanded ? '' : 'rotate-180'} px-2`} />
      </button>
      <div className={`bg-inherit ${isAccordionExpanded ? 'block' : 'hidden'}`}>
        <div className={`bg-inherit flex flex-col gap-2`}>
          {showChatLock && isAdmin && <VyButton onClick={handleToggleChat}>{isChatLocked ? t('chat:unlock') : t('chat:lock')}</VyButton>}
          {showClearChat && chatExportId && isAdmin && (
            <VyConfirm onConfirm={clearChat} title={t('chat:clear-chat')}>
              <VyButton className="w-full">{t('chat:clear-chat')}</VyButton>
            </VyConfirm>
          )}
          {showHighlightsControl && (
            <VyButton onClick={toggleShowHighlights}>{showHighlights ? t('chat:hide-highlights') : t('chat:show-highlights')}</VyButton>
          )}
          {showExport && isAdmin && (
            <VyExportButton
              csvURL={`/meeting/chat/export/${chatExportId}?resType=csv&isStage=${isStage}`}
              xlsxURL={`/meeting/chat/export/${chatExportId}?resType=xlsx&isStage=${isStage}`}
              axiosMethod="GET"
              modalContent={t('event:stage.chat-export-description')}
              className="cursor-pointer"
              body={{ isStage: true }}
              altButton={<VyButton className="w-full">{t('chat:export-chat')}</VyButton>}
            />
          )}
          {showExternalLink && isAdmin && <VyButton onClick={handleSendExternalChatLink}>{t('chat:send-external-link')}</VyButton>}
          {showExternalLink && isAdmin && (
            <VyTooltip title={t('chat:show-qr')}>
              <VyButton className="w-full" onClick={showQr}>
                <p>{t('chat:show-qr')}</p>
              </VyButton>
            </VyTooltip>
          )}
        </div>
      </div>
    </div>
  );
};
